import { emptyRaw, shapeAxis, type RawControls } from './smoothing';

export type Action =
  | 'throttle'
  | 'brake'
  | 'steerLeft'
  | 'steerRight'
  | 'handbrake'
  | 'shiftUp'
  | 'shiftDown'
  | 'camera'
  | 'lookLeft'
  | 'lookRight'
  | 'lookBack'
  | 'reset'
  | 'lights'
  | 'horn'
  | 'pause';

export type KeyBindings = Record<Action, string[]>;

export const DEFAULT_KEYS: KeyBindings = {
  throttle: ['KeyW', 'ArrowUp'],
  brake: ['KeyS', 'ArrowDown'],
  steerLeft: ['KeyA', 'ArrowLeft'],
  steerRight: ['KeyD', 'ArrowRight'],
  handbrake: ['Space'],
  shiftUp: ['KeyE'],
  shiftDown: ['KeyQ'],
  camera: ['KeyC'],
  lookLeft: ['KeyZ'],
  lookRight: ['KeyX'],
  lookBack: ['KeyV'],
  reset: ['KeyR'],
  lights: ['KeyL'],
  horn: ['KeyH'],
  pause: ['Escape', 'KeyP'],
};

/**
 * Gamepad bindings: a button index, or an axis — either one direction of a centred axis (dir), or a
 * calibrated range from `rest` to `full` (pedals and wheels report all sorts of ranges and
 * inversions; the calibration screen records both ends).
 */
export type PadBinding = { button: number } | { axis: number; dir: 1 | -1; rest?: number; full?: number };

export interface PadMapping {
  steerAxis: number;
  /** Calibrated steering range (wheels): full left, centre, full right as raw axis values. */
  steerMin?: number;
  steerCenter?: number;
  steerMax?: number;
  throttle: PadBinding;
  brake: PadBinding;
  handbrake: PadBinding;
  shiftUp: PadBinding;
  shiftDown: PadBinding;
  camera: PadBinding;
  lookBack: PadBinding;
  reset: PadBinding;
  pause: PadBinding;
  lookAxisX: number;
  lookAxisY: number;
  invertThrottle: boolean;
  invertBrake: boolean;
}

export const STANDARD_PAD: PadMapping = {
  steerAxis: 0,
  throttle: { button: 7 },
  brake: { button: 6 },
  handbrake: { button: 0 },
  shiftUp: { button: 5 },
  shiftDown: { button: 4 },
  camera: { button: 3 },
  lookBack: { button: 2 },
  reset: { button: 8 },
  pause: { button: 9 },
  lookAxisX: 2,
  lookAxisY: 3,
  invertThrottle: false,
  invertBrake: false,
};

export interface PadSettings {
  deadZone: number;
  linearity: number;
  rumble: boolean;
}

export interface LookState {
  /** -1..1 yaw offset request (left/right), pitch -1..1; back = look behind. */
  yaw: number;
  pitch: number;
  back: boolean;
}

export function readBinding(p: Pick<Gamepad, 'axes' | 'buttons'>, b: PadBinding): number {
  if ('button' in b) return p.buttons[b.button]?.value ?? 0;
  const v = p.axes[b.axis] ?? 0;
  if (b.rest !== undefined && b.full !== undefined && Math.abs(b.full - b.rest) > 1e-3) return Math.max(0, Math.min(1, (v - b.rest) / (b.full - b.rest)));
  return Math.max(0, v * b.dir);
}

/** Steering axis to −1…1 through the calibrated left/centre/right values when present. */
export function readSteer(p: Pick<Gamepad, 'axes'>, m: PadMapping): number {
  const v = p.axes[m.steerAxis] ?? 0;
  if (m.steerMin === undefined || m.steerMax === undefined) return v;
  const c = m.steerCenter ?? (m.steerMin + m.steerMax) / 2;
  const span = v < c ? c - m.steerMin : m.steerMax - c;
  return Math.abs(span) > 1e-3 ? Math.max(-1, Math.min(1, (v - c) / span)) : 0;
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

function validBinding(b: unknown): PadBinding | null {
  if (!b || typeof b !== 'object') return null;
  const o = b as Record<string, unknown>;
  if (isNum(o.button) && o.button >= 0 && o.button < 64) return { button: Math.round(o.button) };
  if (isNum(o.axis) && o.axis >= 0 && o.axis < 32 && (o.dir === 1 || o.dir === -1)) {
    const r: PadBinding = { axis: Math.round(o.axis), dir: o.dir };
    if (isNum(o.rest) && isNum(o.full)) {
      r.rest = o.rest;
      r.full = o.full;
    }
    return r;
  }
  return null;
}

/** A saved mapping, checked field by field (anything malformed falls back to the standard pad). */
export function validPadMapping(x: unknown): PadMapping {
  const def = structuredClone(STANDARD_PAD);
  if (!x || typeof x !== 'object') return def;
  const o = x as Record<string, unknown>;
  const out = def as unknown as Record<string, unknown>;
  for (const k of ['throttle', 'brake', 'handbrake', 'shiftUp', 'shiftDown', 'camera', 'lookBack', 'reset', 'pause'] as const) {
    const b = validBinding(o[k]);
    if (b) out[k] = b;
  }
  for (const k of ['steerAxis', 'lookAxisX', 'lookAxisY'] as const) if (isNum(o[k]) && (o[k] as number) >= 0 && (o[k] as number) < 32) out[k] = Math.round(o[k] as number);
  if (isNum(o.steerMin) && isNum(o.steerMax)) {
    def.steerMin = o.steerMin;
    def.steerMax = o.steerMax;
    if (isNum(o.steerCenter)) def.steerCenter = o.steerCenter;
  }
  if (typeof o.invertThrottle === 'boolean') def.invertThrottle = o.invertThrottle;
  if (typeof o.invertBrake === 'boolean') def.invertBrake = o.invertBrake;
  return def;
}

/** Saved key bindings: known actions only, 1–3 key codes each, defaults for the rest. */
export function validKeys(x: unknown): KeyBindings {
  const def = structuredClone(DEFAULT_KEYS);
  if (!x || typeof x !== 'object') return def;
  for (const a of Object.keys(def) as Action[]) {
    const v = (x as Record<string, unknown>)[a];
    if (Array.isArray(v) && v.length > 0 && v.length <= 3 && v.every((k) => typeof k === 'string' && /^[A-Za-z0-9]{1,24}$/.test(k))) def[a] = v as string[];
  }
  return def;
}

/** Bind `code` to `action` as its main key, removing it from any other action. */
export function rebindKey(keys: KeyBindings, action: Action, code: string): void {
  for (const a of Object.keys(keys) as Action[]) {
    if (a !== action) keys[a] = keys[a].filter((k) => k !== code);
    if (a !== action && keys[a].length === 0) keys[a] = [...DEFAULT_KEYS[a].filter((k) => !Object.values(keys).flat().includes(k))].slice(0, 1);
  }
  keys[action] = [code, ...keys[action].filter((k) => k !== code)].slice(0, 2);
}

/**
 * Keyboard + gamepad device layer. Produces RawControls each frame plus one-shot UI actions.
 */
export class InputDevices {
  keys: KeyBindings = structuredClone(DEFAULT_KEYS);
  pad: PadMapping = structuredClone(STANDARD_PAD);
  padSettings: PadSettings = { deadZone: 0.08, linearity: 1.4, rumble: true };
  private down = new Set<string>();
  private pressed = new Set<Action>();
  private padPrev = new Map<string, boolean>();
  private mouseLook = { active: false, yaw: 0, pitch: 0 };
  readonly raw: RawControls = emptyRaw();
  readonly look: LookState = { yaw: 0, pitch: 0, back: false };
  /** Index of the gamepad last used (null when none). */
  activePad: number | null = null;
  lastDevice: 'keyboard' | 'gamepad' | 'touch' = 'keyboard';
  onPadConnected: ((id: string) => void) | null = null;
  /** External (touch) control source merged into raw controls when present. */
  touch: RawControls | null = null;
  private on = true;
  /** Off while a menu is open: keys and buttons held across the switch are forgotten. */
  get enabled(): boolean {
    return this.on;
  }
  set enabled(v: boolean) {
    if (v === this.on) return;
    this.on = v;
    this.down.clear();
    this.pressed.clear();
  }

  constructor(target: Window = window) {
    target.addEventListener('keydown', (e) => this.onKey(e, true));
    target.addEventListener('keyup', (e) => this.onKey(e, false));
    target.addEventListener('blur', () => this.down.clear());
    target.addEventListener('gamepadconnected', (e) => {
      const ge = e as GamepadEvent;
      this.onPadConnected?.(ge.gamepad.id);
    });
    target.addEventListener('mousedown', (e) => {
      if (e.button === 2) this.mouseLook.active = true;
    });
    target.addEventListener('mouseup', (e) => {
      if (e.button === 2) {
        this.mouseLook.active = false;
        this.mouseLook.yaw = 0;
        this.mouseLook.pitch = 0;
      }
    });
    target.addEventListener('mousemove', (e) => {
      if (!this.mouseLook.active) return;
      this.mouseLook.yaw = Math.max(-1, Math.min(1, this.mouseLook.yaw - e.movementX * 0.004));
      this.mouseLook.pitch = Math.max(-1, Math.min(1, this.mouseLook.pitch - e.movementY * 0.004));
    });
    target.addEventListener('contextmenu', (e) => {
      const t = e.target as HTMLElement | null;
      if (t && t.tagName === 'CANVAS') e.preventDefault();
    });
  }

  private onKey(e: KeyboardEvent, isDown: boolean): void {
    if (!this.enabled) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    const code = e.code;
    if (isDown) {
      if (!this.down.has(code)) {
        for (const a of Object.keys(this.keys) as Action[]) if (this.keys[a].includes(code)) this.pressed.add(a);
      }
      this.down.add(code);
      this.lastDevice = 'keyboard';
      // Prevent page scroll for driving keys.
      if (code.startsWith('Arrow') || code === 'Space') e.preventDefault();
    } else this.down.delete(code);
  }

  private keyHeld(a: Action): boolean {
    for (const k of this.keys[a]) if (this.down.has(k)) return true;
    return false;
  }

  /** One-shot actions since the last call (camera, reset, lights, horn edge, pause, shifts). */
  consumePressed(a: Action): boolean {
    if (this.pressed.has(a)) {
      this.pressed.delete(a);
      return true;
    }
    return false;
  }

  held(a: Action): boolean {
    return this.keyHeld(a) || this.padHeld(a);
  }

  private padHeldState = new Map<Action, boolean>();
  private padHeld(a: Action): boolean {
    return this.padHeldState.get(a) ?? false;
  }

  /** Poll devices; call once per rendered frame. */
  poll(): void {
    const r = this.raw;
    let steer = (this.keyHeld('steerRight') ? 1 : 0) - (this.keyHeld('steerLeft') ? 1 : 0);
    let throttle = this.keyHeld('throttle') ? 1 : 0;
    let brake = this.keyHeld('brake') ? 1 : 0;
    let hand = this.keyHeld('handbrake') ? 1 : 0;
    let digital = true;
    let shiftUp = this.keyHeld('shiftUp');
    let shiftDown = this.keyHeld('shiftDown');
    this.look.yaw = (this.keyHeld('lookLeft') ? 1 : 0) - (this.keyHeld('lookRight') ? 1 : 0);
    this.look.pitch = 0;
    this.look.back = this.keyHeld('lookBack');
    if (this.mouseLook.active) {
      this.look.yaw = this.mouseLook.yaw;
      this.look.pitch = this.mouseLook.pitch;
    }

    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of pads) {
      if (!p || !p.connected) continue;
      const m = this.pad;
      const s = shapeAxis(readSteer(p, m), this.padSettings.deadZone, this.padSettings.linearity);
      let th = readBinding(p, m.throttle);
      let br = readBinding(p, m.brake);
      if (m.invertThrottle) th = 1 - th;
      if (m.invertBrake) br = 1 - br;
      const hb = readBinding(p, m.handbrake);
      const activity = Math.abs(s) + th + br + hb;
      const anyButton = p.buttons.some((b) => b.pressed);
      if (activity > 0.05 || anyButton) {
        this.activePad = p.index;
        this.lastDevice = 'gamepad';
      }
      if (this.activePad !== p.index) continue;
      if (this.lastDevice === 'gamepad') {
        if (Math.abs(s) > Math.abs(steer) || steer === 0) {
          steer = s;
          digital = false;
        }
        throttle = Math.max(throttle, th);
        brake = Math.max(brake, br);
        hand = Math.max(hand, hb);
        if (th > 0.02 || br > 0.02 || Math.abs(s) > 0.02) digital = false;
      }
      const edge = (name: Action, b: PadBinding) => {
        const v = readBinding(p, b) > 0.5;
        const key = `${p.index}:${name}`;
        const prev = this.padPrev.get(key) ?? false;
        if (v && !prev && this.enabled) this.pressed.add(name);
        this.padPrev.set(key, v);
        this.padHeldState.set(name, v);
      };
      edge('shiftUp', m.shiftUp);
      edge('shiftDown', m.shiftDown);
      edge('camera', m.camera);
      edge('lookBack', m.lookBack);
      edge('reset', m.reset);
      edge('pause', m.pause);
      shiftUp = shiftUp || (this.padHeldState.get('shiftUp') ?? false);
      shiftDown = shiftDown || (this.padHeldState.get('shiftDown') ?? false);
      const lx = shapeAxis(p.axes[m.lookAxisX] ?? 0, 0.2, 1);
      const ly = shapeAxis(p.axes[m.lookAxisY] ?? 0, 0.2, 1);
      if (Math.abs(lx) > 0 || Math.abs(ly) > 0) {
        this.look.yaw = -lx;
        this.look.pitch = -ly;
      }
      if (this.padHeldState.get('lookBack')) this.look.back = true;
    }

    if (this.touch) {
      const t = this.touch;
      if (Math.abs(t.steer) > 0.01 || t.throttle > 0 || t.brake > 0 || t.handbrake > 0) {
        this.lastDevice = 'touch';
        steer = t.steer;
        throttle = Math.max(throttle, t.throttle);
        brake = Math.max(brake, t.brake);
        hand = Math.max(hand, t.handbrake);
        digital = t.digital;
      }
      shiftUp = shiftUp || t.shiftUp;
      shiftDown = shiftDown || t.shiftDown;
    }

    if (!this.enabled) {
      steer = throttle = brake = hand = 0;
      shiftUp = shiftDown = false;
      this.look.yaw = this.look.pitch = 0;
      this.look.back = false;
    }
    r.steer = steer;
    r.throttle = throttle;
    r.brake = brake;
    r.handbrake = hand;
    r.shiftUp = shiftUp;
    r.shiftDown = shiftDown;
    r.digital = digital;
  }

  rumble(strong: number, weak: number, ms: number): void {
    if (!this.padSettings.rumble || this.activePad === null || this.lastDevice !== 'gamepad') return;
    const p = navigator.getGamepads?.()[this.activePad];
    const act = (p as unknown as { vibrationActuator?: { playEffect?: (t: string, o: object) => Promise<unknown> } })?.vibrationActuator;
    act?.playEffect?.('dual-rumble', { duration: ms, strongMagnitude: Math.min(1, strong), weakMagnitude: Math.min(1, weak) }).catch(() => {});
  }

  clearPressed(): void {
    this.pressed.clear();
  }
}
